/**
 * «Rellenar desde el contrato», arriba del alta de inquilino.
 *
 * Se suelta el PDF del contrato y el servidor devuelve lo que ha leído; quien rellena el
 * formulario es el panel de alta, no este componente. Aquí solo se sube el fichero y se cuenta
 * qué ha salido: qué datos se han rellenado, dónde vive según el contrato y los avisos.
 *
 * ES UN BOTÓN, igual que la zona de facturas de Ingresos y gastos: arrastrar no existe en un
 * móvil, así que se puede soltar o pulsar, y hacen lo mismo.
 *
 * EL CONTRATO SE GUARDA AL CREAR, NO AL LEERLO. Leer no deja nada en el servidor; el panel se
 * queda con el último PDF que se ha leído BIEN y, al crear al inquilino, lo sube como su
 * contrato. Es el último y no el primero porque soltar otro rellena el formulario con OTROS
 * datos, y el papel guardado tiene que ser el que dice lo que se ha guardado. Un PDF que no se
 * ha podido leer no sustituye al bueno: los datos del formulario siguen siendo los de aquel.
 */
import { useRef, useState } from 'react';
import type { ContractReadingResponse } from '@naar/shared';
import { ApiError, api } from '../../api/client';
import { Icon } from '../../components/Icon';
import { toast } from '../../components/feedback/Toaster';
import { formatEur, formatShortDate } from '../../lib/format';
import styles from './ContractReader.module.css';

interface ContractReaderProps {
  /** La última lectura, para enseñar su resumen. La guarda el panel, que es quien la reinicia. */
  lectura: ContractReadingResponse | null;
  /** El PDF que se guardará como contrato al crear: el último que se ha leído bien. */
  contrato: File | null;
  onRead: (lectura: ContractReadingResponse, fichero: File) => void;
}

/**
 * Si la lectura vale como contrato: el modelo trae al arrendatario, y sin él lo que se ha
 * soltado no es un contrato del modelo —o es un escaneo, que no se puede leer—.
 */
export function lecturaCorrecta(lectura: ContractReadingResponse): boolean {
  return Boolean(lectura.tenant.fullName || lectura.tenant.idDocument);
}

/** Qué se ha podido rellenar, en palabras: «nombre, documento, teléfono…». */
function camposRellenados({ tenant, guarantor, placement }: ContractReadingResponse): string[] {
  return [
    tenant.fullName && 'nombre',
    tenant.idDocument && 'documento',
    tenant.phone && 'teléfono',
    tenant.email && 'correo',
    placement && (placement.unitId ? 'inmueble y habitación' : 'inmueble'),
    guarantor && 'el avalista en las notas',
  ].filter((campo): campo is string => Boolean(campo));
}

/** `formatEur` con espacio duro: en una frase, «460» y «€» no pueden caer en líneas distintas. */
const euros = (importe: string) => formatEur(importe).replace(' €', ' €');

/** «H4 en C/ Ejemplo 12 · 460 € al mes · fianza 700 € · firmado el 30/06/26, hasta el 30/06/27». */
function resumenDeCondiciones(contrato: ContractReadingResponse['contract']): string | null {
  const partes = [
    contrato.unitCode && contrato.propertyAddress
      ? `${contrato.unitCode} en ${contrato.propertyAddress}`
      : (contrato.unitCode ?? contrato.propertyAddress),
    contrato.monthlyRent && `${euros(contrato.monthlyRent)} al mes`,
    contrato.depositAmount && `fianza de ${euros(contrato.depositAmount)}`,
    // La fecha de fin es la del contrato, no «N meses desde la firma»: decirlo así la contradiría.
    contrato.signedAt &&
      `firmado el ${formatShortDate(contrato.signedAt)}${
        contrato.endDate ? `, hasta el ${formatShortDate(contrato.endDate)}` : ''
      }`,
  ].filter(Boolean);
  return partes.length > 0 ? partes.join(' · ') : null;
}

/** El contrato nombra un sitio que no se reconoce entre los inmuebles: hay que elegirlo a mano. */
function avisoDeUbicacion({ contract, placement }: ContractReadingResponse): string | null {
  const donde =
    contract.unitCode && contract.propertyAddress
      ? `${contract.unitCode} en ${contract.propertyAddress}`
      : contract.propertyAddress;
  if (!donde) return null;
  if (!placement) {
    return `El contrato habla de «${donde}», pero no se parece a ninguno de tus inmuebles: elige el inmueble y la habitación a mano.`;
  }
  if (!placement.unitId && contract.unitCode) {
    return `Es ${placement.propertyName}, pero ninguna de sus habitaciones se llama «${contract.unitCode}»: elige la habitación a mano.`;
  }
  return null;
}

export function ContractReader({ lectura, contrato, onRead }: ContractReaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [leyendo, setLeyendo] = useState(false);

  async function leer(file: File | undefined) {
    if (!file) return;
    setLeyendo(true);
    try {
      onRead(await api.readTenantContract(file), file);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido leer el contrato.');
    } finally {
      setLeyendo(false);
    }
  }

  const condiciones = lectura ? resumenDeCondiciones(lectura.contract) : null;
  const rellenados = lectura ? camposRellenados(lectura) : [];
  const correcta = lectura ? lecturaCorrecta(lectura) : false;
  const ubicacion = lectura && correcta ? avisoDeUbicacion(lectura) : null;

  return (
    <div className={styles.reader}>
      <div
        className={over ? `${styles.drop} ${styles.dropOver}` : styles.drop}
        onDragOver={(event) => {
          event.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setOver(false);
          void leer(event.dataTransfer.files[0]);
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf"
          className={styles.dropInput}
          onChange={(event) => {
            void leer(event.target.files?.[0]);
            // Se limpia para que volver a elegir el MISMO fichero dispare el evento otra vez.
            event.target.value = '';
          }}
        />
        <button
          type="button"
          className={styles.dropButton}
          onClick={() => inputRef.current?.click()}
          disabled={leyendo}
        >
          <Icon name="file" size={15} strokeWidth={2} />
          {leyendo
            ? 'Leyendo el contrato…'
            : lectura
              ? 'Leer otro contrato'
              : 'Suelta aquí el contrato en PDF o púlsalo para elegirlo'}
        </button>
      </div>

      {lectura && (
        <div className={styles.result} role="status">
          <p>
            {correcta && rellenados.length > 0 ? (
              <>
                <Icon name="check" size={14} strokeWidth={2.4} /> Rellenado desde el contrato:{' '}
                {rellenados.join(', ')}. Repásalo antes de crear.
              </>
            ) : (
              'No se ha encontrado ningún dato del inquilino en el contrato.'
            )}
          </p>
          {correcta && condiciones && (
            <p className={styles.terms}>
              <span className={styles.termsLabel}>Condiciones del contrato:</span> {condiciones}
            </p>
          )}
          {contrato && (
            <p className={styles.saved}>
              <Icon name="file-lines" size={14} />{' '}
              {correcta
                ? `Al crearlo se guardará como su contrato: «${contrato.name}».`
                : `Este PDF no se guardará. Se guardará el último que sí se ha leído: «${contrato.name}».`}
            </p>
          )}
        </div>
      )}

      {[...(ubicacion ? [ubicacion] : []), ...(lectura?.warnings ?? [])].map((aviso) => (
        <div key={aviso} className={styles.warning}>
          <Icon name="info" size={15} />
          <span>{aviso}</span>
        </div>
      ))}
    </div>
  );
}
